import React, { ReactNode, useState, cloneElement, isValidElement, useCallback, useEffect } from 'react';
import styles from '../features/time-widget/components/TimeWidgetContainer.module.css';
import { LazyChartsDisplayPanel } from '../common/LazyFeatureLoader';
import { useShallow } from 'zustand/react/shallow';
import useGeoData from '../store/useGeoData';
import type { GeoDataState } from '../store/types';
import AttributeTablePanel from '../features/table/components/AttributeTablePanel';
import './MainLayout.css'; // Creeremo questo file per gli stili
import { FixedFeaturePopup } from '../features/map/components/FixedFeaturePopup'; // Importa il popup fisso
import TimeWidgetContainer from '../features/time-widget/components/TimeWidgetContainer'; // Importa il TimeWidgetContainer

interface MainLayoutProps {
  panelLeft: ReactNode;
  mapCenter: ReactNode;
  panelRight: ReactNode; // Placeholder per il futuro pannello destro
}

const MainLayout: React.FC<MainLayoutProps> = ({
  panelLeft,
  mapCenter,
  panelRight,
}) => {
  const [isChartsDisplayPanelVisible, setIsChartsDisplayPanelVisible] = useState(false);
  const [attributePanelDimensions, setAttributePanelDimensions] = useState({ height: 250, isCollapsed: false });
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const toggleChartsDisplayPanel = () => setIsChartsDisplayPanelVisible(prev => !prev);

  const { openAttributeTableIds, isTimeWidgetActive } = useGeoData(
    useShallow((state: GeoDataState) => ({
      openAttributeTableIds: state.openAttributeTableIds,
      isTimeWidgetActive: state.isTimeWidgetActive,
    }))
  );

  const isAttributeTablePanelVisible = openAttributeTableIds.length > 0;

  const handleAttributePanelDimensionsChange = useCallback((dimensions: { height: number; isCollapsed: boolean }) => {
    setAttributePanelDimensions(dimensions);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const chartsPanelBaseBottomVh = 4.5;
  const spacingBetweenPanelsPx = 10;

  let chartsPanelDynamicStyle: React.CSSProperties = {
    bottom: `${chartsPanelBaseBottomVh}vh`, // Default
  };

  if (isChartsDisplayPanelVisible && isAttributeTablePanelVisible) {
    const currentAttributePanelHeight = attributePanelDimensions.isCollapsed
      ? 40 // Altezza header approx.
      : attributePanelDimensions.height;

    const bottomOffset = currentAttributePanelHeight + spacingBetweenPanelsPx;
    chartsPanelDynamicStyle = {
      bottom: `calc(${chartsPanelBaseBottomVh}vh + ${bottomOffset}px)`,
    };
  }

  // Calcolo di maxHeightForChartsPanel
  const topMarginVh = 4.5; // Spazio desiderato sopra l'area dei pannelli
  // chartsPanelBaseBottomVh è già definito come 4.5vh (spazio sotto)

  const totalVerticalMarginPixels =
    (topMarginVh / 100) * viewportHeight +
    (chartsPanelBaseBottomVh / 100) * viewportHeight;

  const availableHeightForBothPanels = viewportHeight - totalVerticalMarginPixels;

  let maxHeightForChartsPanel: number;

  if (isAttributeTablePanelVisible) {
    const heightOfAttributeTable = attributePanelDimensions.isCollapsed
      ? 40 // Altezza header collassato
      : attributePanelDimensions.height;
    maxHeightForChartsPanel =
      availableHeightForBothPanels - heightOfAttributeTable - spacingBetweenPanelsPx;
  } else {
    maxHeightForChartsPanel = availableHeightForBothPanels;
  }
  // Assicurati che maxHeightForChartsPanel non sia negativo
  maxHeightForChartsPanel = Math.max(0, maxHeightForChartsPanel);


  return (
    <div className="main-layout">
      <div className="main-layout__left">{panelLeft}</div> {/* Classe fissa */}
      <div className="main-layout__center">
        {mapCenter}
        {/* Popup fisso aggiunto qui per avere il corretto contesto di posizionamento */}
        <FixedFeaturePopup />

        {/* Wrapper per TimeWidgetContainer */}
        {isTimeWidgetActive && (
          <div className={styles.timeWidgetWrapper}>
            <TimeWidgetContainer isVisible={isTimeWidgetActive} />
          </div>
        )}

        {/* La tabella degli attributi è ora gestita internamente */}
        {isAttributeTablePanelVisible && <AttributeTablePanel onDimensionsChange={handleAttributePanelDimensionsChange} />}
        <LazyChartsDisplayPanel
          isVisible={isChartsDisplayPanelVisible}
          onClose={() => setIsChartsDisplayPanelVisible(false)}
          style={chartsPanelDynamicStyle} // Applica lo stile dinamico
          dynamicMaxHeight={maxHeightForChartsPanel} // Nuova prop
        />
      </div>
      <div className="main-layout__right">
        {/* Passiamo le props a panelRight, assumendo sia RightPanelContainer */}
        {isValidElement(panelRight) ? cloneElement(panelRight as React.ReactElement<any>, {
          isChartsDisplayVisible: isChartsDisplayPanelVisible,
          onToggleChartsDisplay: toggleChartsDisplayPanel,
        }) : panelRight}
      </div>
    </div>
  );
};

export default MainLayout;